import { Link } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import { fetchChampions, fetchPlayoffSeasons } from '../services/league'
import {
  Avatar,
  Card,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  formatCalendarDate,
  playerProfilePath,
} from '../components/ui'
import './HallOfChampions.css'

export default function HallOfChampions() {
  const champions = useAsync(fetchChampions, [])
  // Only the season numbers are needed, and only to decide which cards get a
  // bracket link; the brackets themselves are never fetched here.
  const playoffSeasons = useAsync(fetchPlayoffSeasons, [])
  const withBracket = new Set(playoffSeasons.data?.map(season => season.seasonNumber))

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">Hall of Champions</span>
          <h1>Every season has one winner</h1>
          <p>The complete list of RTL title holders.</p>
        </div>
      </header>

      {champions.error && <Notice tone="error">{champions.error}</Notice>}

      {champions.loading ? (
        <Loading rows={5} />
      ) : champions.data && champions.data.length > 0 ? (
        <div className="champion-grid">
          {champions.data.map(champion => (
            <article key={champion.id} className="champion">
              <div className="champion__season">
                <span className="champion__season-label">Season</span>
                <span className="champion__season-number">{champion.seasonNumber}</span>
              </div>

              <div className="champion__body">
                <div className="champion__winner">
                  <Avatar
                    name={champion.championName}
                    discordId={champion.championDiscordId}
                    avatar={champion.championAvatar}
                    size="lg"
                  />
                  <div className="champion__winner-text">
                    <span className="champion__label">Champion</span>
                    <div className="champion__name">
                      {champion.championLeaguePlayerId === null ? (
                        champion.championName
                      ) : (
                        <Link
                          className="player__name--link"
                          to={playerProfilePath(champion.championLeaguePlayerId)}
                        >
                          {champion.championName}
                        </Link>
                      )}
                    </div>
                    {champion.championTeamAbbreviation && (
                      <span className="team-tag">{champion.championTeamAbbreviation}</span>
                    )}
                  </div>
                </div>

                <div className="champion__runner-up">
                  <span className="champion__label">Runner-up</span>
                  {champion.runnerUpName ? (
                    <PlayerCell
                      name={champion.runnerUpName}
                      discordId={champion.runnerUpDiscordId}
                      avatar={champion.runnerUpAvatar}
                      to={
                        champion.runnerUpLeaguePlayerId === null
                          ? undefined
                          : playerProfilePath(champion.runnerUpLeaguePlayerId)
                      }
                    />
                  ) : (
                    <span className="champion__unknown">—</span>
                  )}
                </div>

                <dl className="champion__facts">
                  <div>
                    <dt>Series</dt>
                    <dd className="champion__series">{champion.seriesResult ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>Decided</dt>
                    <dd>{formatCalendarDate(champion.decidedOn)}</dd>
                  </div>
                </dl>

                {champion.blurb && <p className="champion__blurb">{champion.blurb}</p>}

                {withBracket.has(champion.seasonNumber) && (
                  <Link
                    className="btn btn--ghost btn--sm champion__bracket"
                    to={`/playoffs/${champion.seasonNumber}`}
                  >
                    View bracket
                  </Link>
                )}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState title="No champions recorded yet">
            Admins can add title holders for seasons 1–32 from the Admin page.
          </EmptyState>
        </Card>
      )}
    </div>
  )
}
